import React, { Suspense, useEffect, useState } from 'react';
import lazyWithRetry from './utils/lazyWithRetry';
import Spinner from './components/Spinner/Spinner';
import ApiBildirim from './components/ApiBildirim/ApiBildirim';
import { BrowserRouter as Router, Route, Routes, useLocation, Navigate, useNavigate } from 'react-router-dom';
import ScrollToTop from './components/ScrollToTop/ScrollToTop';
import './App.css'; // Stil dosyanız
import './styles/eskiCssSirasi'; // FE-34: tüm CSS kod bölmeden önceki sırayla (bkz. dosya başı)
import HomePage from './pages/Homepage/Homepage';
import LoginPage from './pages/LoginPage/LoginPage';
import SignupPage from './pages/SignupPage/SignupPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage/ForgotPasswordPage';
import Sidebar from './components/Sidebar/Sidebar';
import PageBackButton from './components/PageBackButton/PageBackButton';
import { recordHistoryEntry } from './utils/navigationHistory';
import Header from './components/Header/Header';
import ProtectedRoute from './ProtectedRoute';
import { MERCHANT_OVERVIEW_ENABLED } from './utils/merchantPayouts';
import { StoreProvider } from './pages/StorePage/StoreContext';
import { ChatProvider } from './contexts/ChatContext';
import { CustomerLockProvider } from './contexts/CustomerLockContext';
import { StatusSocketProvider } from './contexts/StatusSocketContext';
import { useAuth } from './AuthContext';
import { useIdleLogout } from './utils/useIdleLogout';
import { getMerchantComplianceMessage } from './utils/merchantComplianceMessage';
import { klavyeIleTikla } from "./utils/a11y"; // FE-78: tıklanabilir öğelerde klavye desteği
import { oturumuKapat, tokenVadesiDolduMu } from "./utils/oturum";
import { getPortal } from "./utils/portal";

// FE-34: giriş/anasayfa dışındaki sayfalar ayrı parça; ilk ziyarette yüklenir.
const SatisfactionPage = lazyWithRetry(() => import('./pages/SatisfactionPage/SatisfactionPage'));
const CreateRequestPage = lazyWithRetry(() => import('./pages/CreateRequestPage/CreateRequestPage'));
const MyWarehousesPage = lazyWithRetry(() => import('./pages/MyWarehousesPage/MyWarehousesPage'));
const FAQPage = lazyWithRetry(() => import('./pages/FAQPage/FAQPage'));
const ProfilePage = lazyWithRetry(() => import('./pages/ProfilePage/ProfilePage'));
const MyRequestsPage = lazyWithRetry(() => import('./pages/MyRequestsPage/MyRequestsPage'));
const MyCustomers = lazyWithRetry(() => import('./pages/MyCustomers/MyCustomers'));
const Users = lazyWithRetry(() => import('./pages/Users/Users'));
const UserPermissions = lazyWithRetry(() => import('./pages/UserPermissions/UserPermissions'));
const AdminRequestsPage = lazyWithRetry(() => import('./pages/AdminRequestsPage/AdminRequestsPage'));
const MyOperations = lazyWithRetry(() => import('./pages/MyOperations/MyOperations'));
const WareHouseList = lazyWithRetry(() => import('./pages/WareHouseList/WareHouseList'));
const AdminOffersPage = lazyWithRetry(() => import('./pages/AdminOffersPage/AdminOffersPage'));
const MyOffersPage = lazyWithRetry(() => import('./pages/MyOffersPage/MyOffersPage'));
const OfferDetailForCustomer = lazyWithRetry(() => import('./pages/OfferDetailForCustomer/OfferDetailForCustomer'));
const WarehouseInvitationPage = lazyWithRetry(() => import('./pages/WarehouseInvitationPage/WarehouseInvitationPage'));
const WareHouseFirmList = lazyWithRetry(() => import('./pages/WareHouseFirmList/WareHouseFirmList'));
const MyWarehouseFirm = lazyWithRetry(() => import('./pages/MyWarehouseFirm/MyWarehouseFirm'));
const AdminApplicationsPage = lazyWithRetry(() => import('./pages/AdminApplicationsPage/AdminApplicationsPage'));
const AdditionalServicesPage = lazyWithRetry(() => import('./pages/AdditionalServicesPage/AdditionalServicesPage'));
const PromoCodesPage = lazyWithRetry(() => import('./pages/PromoCodesPage/PromoCodesPage'));
const ListingApprovalsPage = lazyWithRetry(() => import('./pages/ListingApprovalsPage/ListingApprovalsPage'));
const NotificationCenterPage = lazyWithRetry(() => import('./pages/NotificationCenterPage/NotificationCenterPage'));
const StoreListingCreatePage = lazyWithRetry(() => import('./pages/StoreListingCreatePage/StoreListingCreatePage'));
const StoreSettingsPage = lazyWithRetry(() => import('./pages/StoreSettingsPage/StoreSettingsPage'));
const WebsiteEditorPage = lazyWithRetry(() => import('./pages/WebsiteEditorPage/WebsiteEditorPage'));
const EmailNotificationListPage = lazyWithRetry(() => import('./pages/EmailNotificationListPage/EmailNotificationListPage'));
const MerchantTransportRequestsPage = lazyWithRetry(() => import('./pages/MerchantTransportRequestsPage/MerchantTransportRequestsPage'));
const RecommendCustomerPage = lazyWithRetry(() => import('./pages/RecommendCustomerPage/RecommendCustomerPage'));
const ContractsPage = lazyWithRetry(() => import('./pages/ContractsPage/ContractsPage'));
const MyPaymentsPage = lazyWithRetry(() => import('./pages/MyPaymentsPage/MyPaymentsPage'));
const PaymentResultPage = lazyWithRetry(() => import('./pages/PaymentResultPage/PaymentResultPage'));
const PaymentsPage = lazyWithRetry(() => import('./pages/PaymentsPage/PaymentsPage'));
const MerchantInformationPage = lazyWithRetry(() => import('./pages/MerchantInformationPage/MerchantInformationPage'));
const InformationPage = lazyWithRetry(() => import('./pages/InformationPage/InformationPage'));
const TroyiAssistant = lazyWithRetry(() => import('./pages/TroyiAssistant/TroyiAssistant'));
const Overview = lazyWithRetry(() => import('./pages/Overview/Overview'));
const MerchantOverview = lazyWithRetry(() => import('./pages/MerchantOverview/MerchantOverview'));
const TroyiDashboard = lazyWithRetry(() => import('./pages/TroyiDashboard/TroyiDashboard'));
const TroyiConversations = lazyWithRetry(() => import('./pages/TroyiConversations/TroyiConversations'));
const Chat = lazyWithRetry(() => import('./pages/Chat/Chat'));
const StorePage = lazyWithRetry(() => import('./pages/StorePage/StorePage'));

function App() {
  return (
    <Router>
      <Layout />
    </Router>
  );
}

function Layout() {
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  // Müşteri ilk girişte geçici şifreyle geldiyse üstte şifre-yenileme bandı gösterilir;
  // şifre değişince (ProfilePage "password-renewal-done" event'i) bant kalkar.
  const [passwordRenewalPending, setPasswordRenewalPending] = useState(
    () => { try { return localStorage.getItem("passwordRenewalPending") === "1"; } catch (e) { return false; } }
  );
  useEffect(() => {
    const sync = () => {
      try { setPasswordRenewalPending(localStorage.getItem("passwordRenewalPending") === "1"); } catch (e) { /* yoksay */ }
    };
    window.addEventListener("password-renewal-done", sync);
    window.addEventListener("storage", sync);
    return () => { window.removeEventListener("password-renewal-done", sync); window.removeEventListener("storage", sync); };
  }, []);
  const location = useLocation();
  const navigate = useNavigate();
  const {
    userType, logout, showMerchantComplianceWarning, status: authStatus,
    // Bant metni artık EKSİK KALEME göre yazılıyor (bkz. merchantComplianceMessage).
    complianceBreakdown, agreementStatus, merchantDocumentsApproved,
  } = useAuth();
  const complianceMessage = getMerchantComplianceMessage(complianceBreakdown, {
    agreementApproved: String(agreementStatus ?? "").trim() === "1",
    documentsApproved: merchantDocumentsApproved !== false,
  });

  const toggleSidebar = () => {
    setSidebarOpen(!isSidebarOpen);
  };

  // Mobilde: bir sayfaya (menü öğesine) geçildiğinde sidebar kapanır. Route değişimini
  // dinlemek tüm navigasyonları (menü linki, programatik yönlendirme, ileri/geri) kapsar;
  // accordion aç/kapa route değiştirmediği için sidebar açık kalır. Desktop'ta no-op
  // (.open sınıfı yalnız ≤768px'de etkili).
  useEffect(() => {
    setSidebarOpen(false);
    // Giriş sonrası (login → homepage) bayrak yeni yazılmış olabilir: rota değişince tazele.
    try { setPasswordRenewalPending(localStorage.getItem("passwordRenewalPending") === "1"); } catch (e) { /* yoksay */ }
  }, [location.pathname]);

  // "← Geri" için: bu geçmiş girişinde hangi sayfa var, not edilir (giriş ekranı dahil). Buton
  // önceki sayfa giriş/kayıt ekranıysa geri GİTMEZ — giriş ekranı açılınca oturum düşüyordu.
  useEffect(() => {
    recordHistoryEntry(window.history.state, location.pathname);
  }, [location.key, location.pathname]);

  // Oda yetkilendirme davet sayfası (/warehouse-invitation/:id) GİRİŞSİZ ve menüsüzdür: davet edilen
  // kişinin henüz hesabı/şifresi olmayabilir (teklif sayfasıyla aynı muamele).
  const isStandalonePublicPage = location.pathname.includes("/offer-detail-customer") || location.pathname.startsWith("/warehouse-invitation/");
  const showSidebar = location.pathname !== "/" && location.pathname !== "/signup" && location.pathname !== "/forgot-password" && !isStandalonePublicPage; // Eğer route "/login" ise Sidebar gizlenir
  const isAuth = location.pathname === "/" || location.pathname === "/signup" || location.pathname === "/forgot-password";
  const isAuthenticated = !!sessionStorage.getItem("token") || !!localStorage.getItem("token");

  const performLogout = () => {
    // Token/ad/soyad/userID/vade temizlenir + token sunucuda iptal edilir (utils/oturum.js).
    oturumuKapat();
    logout?.();
    navigate("/", { replace: true });
  };

  const { reset: resetIdleTimer } = useIdleLogout({
    enabled: !isAuth && isAuthenticated,
    onIdle: performLogout,
    // timeoutMs: 15_000, // istersek burada override edebiliriz
  });

  // FE-77: kullanıcının token'ı API'de geçersizse (süresi doldu / başka yerden çıkış) çıkış yap; giriş ekranı
  // "Oturumunuz sona erdi" der (LoginForm sessionStorage "oturumBitti").
  useEffect(() => {
    const dustu = () => {
      try { sessionStorage.setItem("oturumBitti", "1"); } catch (e) { /* yoksay */ }
      performLogout();
    };
    window.addEventListener("oturum-dustu", dustu);
    return () => window.removeEventListener("oturum-dustu", dustu);
  });

  // Güvenlik manifesti A4: giriş token'ı 30 gün; saklı vade (expiredTimestamp) dolmadan çıkış yaptırılır —
  // açılışta, sekmeye dönüldüğünde ve 5 dakikada bir bakılır. Vade bilinmiyorsa (eski oturum) FE-77 kalır.
  useEffect(() => {
    if (isAuth || !isAuthenticated) return undefined;
    const bak = () => {
      if (!tokenVadesiDolduMu()) return;
      try { sessionStorage.setItem("oturumBitti", "1"); } catch (e) { /* yoksay */ }
      performLogout();
    };
    bak();
    const t = setInterval(bak, 5 * 60 * 1000);
    document.addEventListener("visibilitychange", bak);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", bak); };
  });

  // Sayfa değiştiğinde de (route change) aktivite kabul edip süreyi sıfırla
  useEffect(() => {
    resetIdleTimer?.();
  }, [location.pathname, resetIdleTimer]);

  return (
    <StatusSocketProvider>
    <ChatProvider>
    <CustomerLockProvider>
    {/* Mağaza sepet/favori durumu: header'daki sepet rozeti ve Profil › Mağaza da okur. */}
    <StoreProvider>
    <div className="app">
      {/* Sayfa değişiminde kaydırma konumu başa döner (bkz. ScrollToTop). */}
      <ScrollToTop />
      <ApiBildirim />
      {showSidebar && <Header toggleSidebar={toggleSidebar} />}
      {showSidebar && <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />}
      {isAuth ? (
        <Routes>
          <Route path="/" element={<LoginPage />} />
          {/* Admin portalında (panel.) kayıt yok: /signup girişe döner. */}
          <Route path="/signup" element={getPortal() === "admin" ? <Navigate to="/" replace /> : <SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      ) : (
        <div className={`main ${isStandalonePublicPage ? "no-sidebar-margin" : ""}`}
          onClick={() => setSidebarOpen(false)}>
          {!isStandalonePublicPage && authStatus === "ready" && showMerchantComplianceWarning && (
            <div className="merchant-compliance-banner" role="alert">
              <span style={{ fontWeight: 700 }}>Uyarı:</span>&nbsp;{complianceMessage}
            </div>
          )}
          {!isStandalonePublicPage && userType === "customer" && passwordRenewalPending && (
            <div className="merchant-compliance-banner" role="alert">
              <span style={{ fontWeight: 700 }}>Uyarı:</span>&nbsp;Güvenliğiniz için e-postanıza gönderilen geçici şifrenizi yenileyin.&nbsp;
              <span role="button" tabIndex={0} onKeyDown={klavyeIleTikla}
                style={{ fontWeight: 700, textDecoration: "underline", cursor: "pointer" }}
                onClick={() => navigate('/profile', { state: { profileTab: "security" } })}
              >
                Şifreni Yenile
              </span>
            </div>
          )}
          {/* Tüm panel sayfalarında "← Geri" (bir önceki sayfa). Herkese açık teklif sayfasında yok. */}
          {showSidebar && <PageBackButton />}
          <Suspense fallback={<div className="route-loading" role="status" aria-label="Yükleniyor"><Spinner /></div>}>
          <Routes>
            <Route path="*" element={<Navigate to="/" replace />} />
            <Route path="/offer-detail-customer" element={<OfferDetailForCustomer />} />
            <Route path="/offer-detail-customer/:offerID" element={<OfferDetailForCustomer />} />
            <Route path="/warehouse-invitation/:invitationID" element={<WarehouseInvitationPage />} />
            <Route path="/homepage" element={<ProtectedRoute element={<HomePage />} />} />
            <Route path="/satisfaction" element={<ProtectedRoute element={<SatisfactionPage />} />} />
            <Route path="/create-request" element={<ProtectedRoute element={<CreateRequestPage />} />} />
            <Route path="/create-request/:customerID" element={<ProtectedRoute element={<CreateRequestPage />} />} />
            <Route path="/create-request/form" element={<ProtectedRoute element={<CreateRequestPage />} />} />
            <Route path="/create-request/:customerID/form" element={<ProtectedRoute element={<CreateRequestPage />} />} />
            <Route path="/my-requests" element={<ProtectedRoute element={<MyRequestsPage />} />} />
            <Route path="/my-offers" element={<ProtectedRoute element={<MyOffersPage />} />} />
            <Route path="/my-contracts" element={<ProtectedRoute element={<ContractsPage />} />} />
            <Route path="/my-contracts/:operationID" element={<ProtectedRoute element={<ContractsPage />} />} />
            <Route path="/customer-contracts/:customerID" element={<ProtectedRoute element={<ContractsPage />} />} />
            {/* Admin ve merchant için sözleşme listesi — kapsam backend tarafından role göre süzülür. */}
            <Route path="/contracts" element={<ProtectedRoute element={<ContractsPage />} />} />
            {/* Ödemeler: müşteri kendi carisi; admin/merchant müşteri listesi + detay */}
            <Route path="/my-payments" element={<ProtectedRoute element={<MyPaymentsPage />} />} />
            {/* 3DS dönüş adresi: /payment-result?orderId=... (Lidio callbackUrl) */}
            <Route path="/payment-result" element={<ProtectedRoute element={<PaymentResultPage />} />} />
            <Route path="/payments" element={<ProtectedRoute element={<PaymentsPage />} />} />
            <Route path="/payments/:customerID" element={<ProtectedRoute element={<PaymentsPage />} />} />
            {/* Ödemeler > üç nokta > Faturalar: müşterinin faturaları (role göre sekmeli) */}
            <Route path="/payments/:customerID/invoices" element={<ProtectedRoute element={<PaymentsPage />} />} />
            <Route path="/my-operations" element={<ProtectedRoute element={<MyOperations />} />} />
            <Route path="/my-operations/:operationID" element={<ProtectedRoute element={<MyOperations />} />} />
            <Route path="/admin-requests" element={<ProtectedRoute element={<AdminRequestsPage />} />} />
            <Route path="/admin-offers" element={<ProtectedRoute element={<AdminOffersPage />} />} />
            <Route path="/additional-services" element={<ProtectedRoute element={<AdditionalServicesPage />} />} />
            <Route path="/promo-codes" element={<ProtectedRoute element={<PromoCodesPage />} />} />
            <Route path="/listing-approvals" element={<ProtectedRoute element={<ListingApprovalsPage />} />} />
            <Route path="/notification-center" element={<ProtectedRoute element={<NotificationCenterPage />} />} />
            <Route path="/listing-create" element={<ProtectedRoute element={<StoreListingCreatePage />} />} />
            <Route path="/store-settings" element={<ProtectedRoute element={<StoreSettingsPage />} />} />
            <Route path="/website-editor" element={<ProtectedRoute element={<WebsiteEditorPage />} />} />
            <Route path="/email-notifications" element={<ProtectedRoute element={<EmailNotificationListPage />} />} />
            <Route path="/admin-requests/:customerID" element={<ProtectedRoute element={<AdminRequestsPage />} />} />
            <Route path="/my-warehouses" element={<ProtectedRoute element={<MyWarehousesPage />} />} />
            <Route path="/my-warehouses/:userID" element={<ProtectedRoute element={<MyWarehousesPage />} />} />
            <Route path="/faq" element={<ProtectedRoute element={<FAQPage />} />} />
            <Route path="/merchant-information" element={<ProtectedRoute element={<MerchantInformationPage />} />} />
            <Route path="/information" element={<ProtectedRoute element={<InformationPage />} />} />
            <Route path="/ask-troyi" element={<ProtectedRoute element={<TroyiAssistant />} />} />
            <Route path="/profile" element={<ProtectedRoute element={<ProfilePage />} />} />
            {/* FE-86: eski /payment-methods sayfası silindi (sahte kart listesiydi); ProtectedRoute eski bağlantıyı
                Ödemelerim › Kayıtlı Kartlarım'a yönlendirmeye devam eder. */}
            <Route path="/payment-methods" element={<ProtectedRoute element={null} />} />
            <Route path="/my-customers" element={<ProtectedRoute element={<MyCustomers />} />} />
            <Route path="/users" element={<ProtectedRoute element={<Users />} />} />
            <Route path="/user-permissions" element={<ProtectedRoute element={<UserPermissions />} />} />
            <Route path="/admin-applications" element={<ProtectedRoute element={<AdminApplicationsPage />} />} />
            <Route path="/firm-list" element={<ProtectedRoute element={<WareHouseFirmList />} />} />
            {/* ESKİ URL — /warehouse-firm-list 21.09.2026'da /firm-list oldu. Yer imleri ve
                dışarıdan gelen bağlantılar kırılmasın diye kalıcı yönlendirme bırakıldı.
                Bilerek ProtectedRoute'suz: hedef zaten korumalı, burada ikinci kapı kurmak
                yetkisiz kullanıcıyı yanlış sayfaya düşürürdü. */}
            <Route path="/warehouse-firm-list" element={<Navigate to="/firm-list" replace />} />
            <Route path="/warehouse-list" element={<ProtectedRoute element={<WareHouseList />} />} />
            <Route path="/my-warehouse-firm" element={<ProtectedRoute element={<MyWarehouseFirm />} />} />
            <Route path="/my-warehouse-firm/my-documents" element={<ProtectedRoute element={<MyWarehouseFirm />} />} />
            <Route path="/transport-requests" element={<ProtectedRoute element={<MerchantTransportRequestsPage />} />} />
            <Route path="/transport-requests/:requestID" element={<ProtectedRoute element={<MerchantTransportRequestsPage />} />} />
            <Route path="/recommend-customer" element={<ProtectedRoute element={<RecommendCustomerPage />} />} />
            <Route path="/overview" element={<ProtectedRoute element={<Overview />} />} />
            {/* Partner Genel Bakış şimdilik gizli: adres anasayfaya döner ("*" rotası giriş ekranına
                atıp oturumu düşürürdü). Açmak için utils/merchantPayouts MERCHANT_OVERVIEW_ENABLED. */}
            <Route
              path="/merchant-overview"
              element={MERCHANT_OVERVIEW_ENABLED
                ? <ProtectedRoute element={<MerchantOverview />} />
                : <Navigate to="/homepage" replace />}
            />
            <Route path="/chat" element={<ProtectedRoute element={<Chat />} />} />
            {/* 2. El Mağaza (website /store'un panel karşılığı) — müşteri ve merchant. Alt rotalar
                StorePage içinde (kategori, ürün, sepet, hesap…). */}
            <Route path="/store/*" element={<ProtectedRoute element={<StorePage />} />} />
            <Route path="/troyi" element={<ProtectedRoute element={<TroyiDashboard />} />} />
            <Route path="/troyi/conversations" element={<ProtectedRoute element={<TroyiConversations />} />} />
            <Route path="/troyi/conversations/:conversationId" element={<ProtectedRoute element={<TroyiConversations />} />} />
          </Routes>
          </Suspense>
        </div>
      )}
    </div>
    </StoreProvider>
    </CustomerLockProvider>
    </ChatProvider>
    </StatusSocketProvider>
  );
}


export default App;
